/**
 * ENG-6017: shared Claude Code TUI dialog detection + dismissal.
 *
 * Extracted from persistent-session.ts's acceptDialogs() cascade so the
 * dialog knowledge has exactly one home, consumable from:
 *
 *  - `acceptDialogs()` in persistent-session.ts — the spawn-time loop that
 *    walks an agent through the first-run dialog cascade (theme → trust →
 *    MCP → bypass …).
 *  - `channel-input-watchdog.ts` — the per-poll-cycle watchdog that fires
 *    Enter at stuck channel input. Before ENG-6017 the watchdog was
 *    dialog-blind: when Claude Code's session-feedback dialog ("How is
 *    Claude doing this session?") overlaid the pane, the watchdog's
 *    single-shot Enter went into the dialog instead of the input box and
 *    the inbound message sat unsubmitted for 40+ minutes (koda,
 *    2026-06-04) while every health metric stayed green.
 *  - `injectMessageWithStatus()` — pre-send pane hygiene on the tmux
 *    send-keys fallback path.
 *
 * Living in its own module (rather than persistent-session.ts) breaks the
 * import cycle persistent-session → channel-input-watchdog →
 * persistent-session that a shared export would otherwise create.
 *
 * DEFAULT-DENY: `sweepDialogs()` only ever returns an action for an
 * explicitly recognised dialog. An unknown dialog gets `null` — never a
 * blind Enter — because a future dialog's default option could be
 * destructive. Callers that want visibility into unknown overlays should
 * log the pane themselves.
 */

import { execFileSync } from 'node:child_process';

/**
 * A recognised dialog plus the keystrokes that dismiss it.
 *
 * `keys` are tmux key names sent as individual `tmux send-keys` calls —
 * never batched into one call, so a multi-byte sequence can't get wrapped
 * into a single bracketed paste (the CSI-u trap documented on
 * defaultArmSender in persistent-session.ts).
 */
export interface DialogAction {
  kind:
    | 'theme-picker'
    | 'folder-trust'
    | 'resume-mode'
    | 'dev-channels'
    | 'mcp-servers'
    | 'bypass-permissions'
    | 'session-feedback'
    | 'usage-limit-choice'
    | 'consumer-terms';
  /** tmux key names, one send-keys invocation each. */
  keys: readonly string[];
  /** Delay between consecutive key sends (selector dialogs need a beat
   * between picking an option and confirming it). */
  interKeyDelayMs: number;
  /** Past-tense log fragment, e.g. "Auto-accepted theme picker". Callers
   * append their own context (`for '<codeName>'` etc.). */
  logMessage: string;
}

/**
 * Detect whether Claude Code is showing the **login picker** dialog.
 *
 * ENG-4634: this dialog appears when ~/.claude.json is missing or
 * Claude Code can't validate the saved session. Pressing Enter on
 * the default (1. Claude account with subscription) kicks off a
 * browser-based OAuth flow that an unattended agent can't complete —
 * the helper used to fall through to the generic `❯ no Enter to
 * confirm` exit branch and declare the session "ready" while the
 * actual claude REPL was still on the picker. Without explicit
 * detection, every manager respawn would silently flip the agent
 * back to the picker and never recover.
 *
 * Pattern matches the literal option strings claude renders. Both
 * 'Claude account with subscription' and 'Anthropic Console account'
 * are present on the picker (and not in the post-login UI), so the
 * conjunction is unambiguous.
 *
 * NOT part of sweepDialogs() — the picker must never be keyed past
 * (it needs an operator OAuth); acceptDialogs() handles it specially.
 */
export function isLoginPickerVisible(screen: string): boolean {
  return (
    screen.includes('Select login method') ||
    (screen.includes('Claude account with subscription') &&
      screen.includes('Anthropic Console account'))
  );
}

/**
 * ENG-10619: the row we ALWAYS pick on Claude Code's consumer-terms dialog.
 *
 *   Updates to Consumer Terms and Policies
 *   …
 *   ❯ 1. Accept terms · Help improve our AI models: ON
 *     2. Accept terms · Help improve our AI models: OFF
 *
 *   Enter to confirm · Esc to cancel
 *
 * The cursor defaults to ON, which opts the account's chats and coding
 * sessions into model training and extends retention from 30 days to 5
 * years. A bare Enter would therefore consent on the customer's behalf, so
 * this row is selected by NAME (selectRowKeys) and never by position or
 * default. The `·` is U+00B7, the same separator Claude Code uses in its
 * confirm footer.
 */
export const CONSUMER_TERMS_OFF_OPTION =
  'Accept terms \u00b7 Help improve our AI models: OFF';

/**
 * ENG-10619: the consumer-terms dialog is on screen. Keyed on the
 * conjunction of the heading and the OFF row's own text so prose that
 * mentions either alone does not match; selectRowKeys still requires a
 * focused modal and a complete option row before anything is keyed.
 */
export function isConsumerTermsDialogVisible(screen: string): boolean {
  return (
    screen.includes('Updates to Consumer Terms') &&
    screen.includes(CONSUMER_TERMS_OFF_OPTION)
  );
}

/**
 * ENG-10619: keys that pick "Help improve our AI models: OFF" on a focused
 * consumer-terms dialog, or null (answer nothing) when that row cannot be
 * resolved. Shared by sweepDialogs() and the Claude pair/re-login flow,
 * which drives its own dialog loop.
 */
export function consumerTermsOffKeys(screen: string): readonly string[] | null {
  if (!isConsumerTermsDialogVisible(screen)) return null;
  return selectRowKeys(screen, CONSUMER_TERMS_OFF_OPTION);
}

/**
 * Detect Claude Code's resume-mode dialog (ENG-5364).
 *
 * On `claude --resume <uuid>` against a transcript large enough to
 * trigger Claude Code 2.1.x's context-management heuristic, the agent
 * lands on an interactive picker offering:
 *
 *   ❯ 1. Resume from summary (recommended)
 *     2. Resume full session as-is
 *     3. Don't ask me again
 *
 * Without auto-dismissal the agent sits silently waiting for keyboard
 * input on every manager respawn — channel inbounds stack up while
 * health metrics stay green. Surfaced fleet-wide on 2026-05-20
 * (don/stirling/maven hit it on a single manager restart).
 *
 * Match on the conjunction of two distinct option strings so a
 * passing mention of "Resume" in a transcript doesn't false-positive.
 */
export function isResumeModeDialogVisible(screen: string): boolean {
  return (
    screen.includes('Resume from summary') &&
    screen.includes("Don't ask me again")
  );
}

/**
 * Detect Claude Code's session-feedback dialog (ENG-6017).
 *
 * After some turns Claude Code renders an optional rating prompt:
 *
 *   ● How is Claude doing this session? (optional)
 *     1: Bad    2: Fine   3: Good   0: Dismiss
 *
 * It waits for a digit — Enter does nothing — so any injected message
 * sits in the input box unsubmitted, and the channel-input-watchdog's
 * Enter is swallowed too. Observed live on koda (agt-aws-1) 2026-06-04:
 * an operator Slack DM sat typed-but-unsubmitted for 40+ minutes behind
 * this dialog while pane-activity / synthetic-probe / heartbeat all
 * stayed green.
 *
 * Match the question text together with the literal `0: Dismiss` option
 * so a transcript merely *quoting* the question doesn't false-positive.
 */
export function isSessionFeedbackDialogVisible(screen: string): boolean {
  return (
    screen.includes('How is Claude doing this session') &&
    screen.includes('0: Dismiss')
  );
}

/**
 * The stop-the-session safe option (ENG-8213). Exported so tests and callers
 * assert against the same literal the matcher uses, rather than a copy that
 * could drift.
 *
 * Kept as the name it has always had because callers and tests reference it,
 * but it is no longer the ONLY option this module selects — see
 * `USAGE_LIMIT_SAFE_OPTIONS` for the full preference order.
 */
export const USAGE_LIMIT_SAFE_OPTION = 'Stop and wait for limit to reset';

/**
 * The auto-continue option, added by Claude Code on the Team seat spend-limit
 * variant of this modal:
 *
 *   2. Wait here, then continue automatically at Aug 21, 10am
 *
 * A PREFIX, deliberately: the row carries a reset date that changes every
 * time, so the literal can only ever be the stable leading text.
 */
export const USAGE_LIMIT_CONTINUE_OPTION = 'Wait here, then continue automatically';

/**
 * Options this module is willing to press, MOST PREFERRED FIRST.
 *
 * `Wait here, then continue automatically` outranks `Stop and wait for limit to
 * reset` because the two differ in exactly the way that matters to an
 * unattended agent: both idle until the cap lifts, but only the first RESUMES
 * by itself afterwards. "Stop and wait" leaves the session parked until a human
 * touches it, which is the state this whole handler exists to avoid — we would
 * have answered the modal and still needed the human.
 *
 * Neither spends money, which is the invariant that governs this list. Every
 * other row on either variant of this modal is excluded on purpose:
 *
 *   - `Switch to usage credits`  — starts pay-as-you-go spend.
 *   - `Switch to Team plan`      — a plan change.
 *   - `Ask your admin for more usage` — spends nothing, but it pages a human
 *     and asks them to raise a budget. That is a decision with a cost attached
 *     and it is not ours to make automatically; a human choosing to raise the
 *     cap is a different act from a fleet of agents asking them to.
 */
export const USAGE_LIMIT_SAFE_OPTIONS: readonly string[] = [
  USAGE_LIMIT_CONTINUE_OPTION,
  USAGE_LIMIT_SAFE_OPTION,
];

/**
 * Every option label known to appear on this modal, including the ones we would
 * never press. This is the RECOGNITION vocabulary, and it is deliberately wider
 * than the selection vocabulary above.
 *
 * ENG-8213 anchored recognition on the BILLING rows alone, which tied "is this
 * the usage-limit modal?" to options that only exist on the subscription
 * variant. Claude Code's Team seat spend-limit variant renders
 *
 *   ❯ 1. Stop and wait for limit to reset
 *     2. Wait here, then continue automatically at Aug 21, 10am
 *     3. Ask your admin for more usage
 *
 * with no billing row at all, so the modal was not recognised — and the failure
 * was not a no-op. This pane renders "Enter to confirm", and the `mcp-servers`
 * branch below fires on `'Enter to confirm' && 'MCP'` with "MCP" trivially
 * present in any agent's scrollback. So the unrecognised variant fell through to
 * a BARE ENTER, confirming whichever row the cursor sat on — row 1, "Stop and
 * wait", the one option that guarantees a human is needed. Observed on
 * acquire-intelligence, 2026-08-19.
 *
 * Recognising by "an option row this modal is known to render" rather than by
 * billing vocabulary keeps the structural guard (it must still be a numbered
 * ROW inside the block adjacent to the nearest footer) while covering both
 * variants and any future one whose rows we add here.
 */
const USAGE_LIMIT_KNOWN_OPTIONS: readonly string[] = [
  ...USAGE_LIMIT_SAFE_OPTIONS,
  'Switch to usage credits',
  'Switch to Team plan',
  // ENG-9006: the two-option variant Claude Code shipped ~2026-08-17. Filed
  // with a wedged pane and four unanswered customer Telegram messages queued
  // behind it.
  'Upgrade your plan',
  'Ask your admin for more usage',
];

/**
 * Detect Claude Code's usage-limit choice dialog (ENG-8213).
 *
 * On hitting the plan limit, Claude Code blocks the TUI on:
 *
 *   What do you want to do?
 *
 *   ❯ 1. Stop and wait for limit to reset
 *     2. Switch to usage credits
 *     3. Switch to Team plan
 *
 *   Enter to confirm · Esc to cancel
 *
 * Nothing answered it, so the session sat on the modal and the agent was
 * unavailable until a human attached to the pane.
 *
 * Matched STRUCTURALLY: a KNOWN option (USAGE_LIMIT_KNOWN_OPTIONS) must appear
 * as a numbered option ROW (`<indent>[❯ ]<digit>. <text>`), not merely
 * somewhere in the capture, and the modal's confirm affordance must be present.
 *
 * The anchor was originally the BILLING rows specifically, which silently made
 * recognition subscription-only and let the Team seat spend-limit variant fall
 * through to a bare Enter. See USAGE_LIMIT_KNOWN_OPTIONS. The structural
 * requirement below is unchanged and is what actually does the safety work; only
 * the vocabulary widened.
 *
 * The looser "billing phrase anywhere && 'Enter to confirm'" version was
 * actively dangerous, and in the opposite direction to the bug this fixes.
 * "Enter to confirm" is rendered by OTHER dialogs (the MCP confirm among
 * them), and an agent that merely writes the words "Switch to usage credits"
 * - discussing a capped teammate, quoting this very file - leaves them in the
 * scrollback. Both true at once and this predicate fires on a pane that is
 * not this dialog. Because sweepDialogs() checks it FIRST and then finds no
 * safe option row, it would return null AND isUnanswerableUsageLimitDialog()
 * would tell every caller to hold the pane: the real dialog never gets
 * answered and the agent wedges. That is the ENG-8194 self-gating shape with
 * a worse blast radius, so the match has to key on layout, not vocabulary.
 */
/**
 * How far above the "Enter to confirm" footer an option row may sit and still
 * count as part of the SAME modal.
 *
 * The real dialog puts its furthest option 4 lines above the footer, so 6
 * carries the whole block with room for a wrap. Deliberately tight: every line
 * of slack here is scrollback that a numbered write-up could occupy, and the
 * cost of being too generous (wedging the pane on a false positive) is worse
 * than the cost of being too tight (failing to answer, which is the status quo
 * this handler improves on).
 */
const USAGE_LIMIT_BLOCK_LINES = 6;

function optionRowDigit(line: string, label: string): string | null {
  const m = line.match(
    new RegExp(
      String.raw`^[^\S\n]*(?:❯[^\S\n]*)?(\d)\.[^\S\n]*` +
        label.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`),
    ),
  );
  return m?.[1] ?? null;
}

/**
 * Return the modal's own lines, or null when this dialog is not on screen.
 *
 * The match is bound to a REGION, not to the capture as a whole: a known
 * option row and the confirm footer must belong to the same block. Two earlier
 * versions of this predicate were too loose, in the same direction each time:
 *
 *   1. "billing phrase anywhere && 'Enter to confirm' anywhere" - satisfied by
 *      prose about a capped teammate sitting behind an unrelated dialog.
 *   2. "billing option ROW anywhere && 'Enter to confirm' anywhere" - still
 *      satisfied by a numbered list in prose (`1. Switch to Team plan`, which
 *      is exactly how an agent writes up this very ticket) plus a confirm
 *      footer from a different dialog.
 *
 * Both misfire the same way, and it is the dangerous way: sweepDialogs() checks
 * this branch FIRST (so the mcp-servers branch cannot bare-Enter the real
 * modal), so a false positive with no safe option row returns null AND makes
 * isUnanswerableUsageLimitDialog() tell every caller to hold the pane. The
 * dialog actually on screen never gets answered and the agent wedges.
 */
/**
 * The lines of the FOCUSED modal — the block immediately above the nearest
 * "Enter to confirm" footer — or null when no modal is focused.
 *
 * Extracted from usageLimitModalBlock so every structural predicate reads the
 * same region by the same rule. "Nearest footer" is load-bearing and was
 * arrived at the hard way: with a newer dialog on screen and an older one still
 * in the scrollback, a backwards search skips the live dialog's footer and
 * matches the STALE one, so we would classify the pane as a dialog that is not
 * focused and key whatever actually is.
 */
function focusedModalBlock(screen: string, blockLines: number): string[] | null {
  const lines = screen.split('\n');
  let footer = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i]!.includes('Enter to confirm')) {
      footer = i;
      break;
    }
  }
  if (footer < 0) return null;
  return lines.slice(Math.max(0, footer - blockLines), footer);
}

/**
 * The MCP-servers dialog's own option rows.
 *
 *   New MCP servers found in .mcp.json
 *   ❯ 1. Use this and all future MCP servers in this project
 *     2. Continue without using these MCP servers
 *
 *   Enter to confirm · Esc to cancel
 */
const MCP_DIALOG_OPTIONS: readonly string[] = [
  'Use this and all future MCP servers in this project',
  'Continue without using these MCP servers',
];

/**
 * Detect the MCP-servers dialog STRUCTURALLY.
 *
 * This branch used to read `screen.includes('Enter to confirm') &&
 * screen.includes('MCP')`, which is not a detector — it is a catch-all. Every
 * agent pane carries the string "MCP" somewhere (`Connected to MCP server:
 * slack` is printed at startup and never scrolls out of a quiet pane), so the
 * condition reduced to "any focused confirm dialog at all", and the action is a
 * BARE ENTER on whatever row the cursor sits on.
 *
 * That is not a hypothetical. It is the mechanism behind the acquire-intelligence
 * wedge on 2026-08-19: the spend-limit modal was not recognised by the
 * usage-limit branch, fell to this one, and got an Enter on the pre-selected
 * "Stop and wait for limit to reset". ENG-8213's fix for the SAME hazard was to
 * order the usage-limit branch first — correct, and only a fix for the one
 * dialog anybody had thought of.
 *
 * It also silently defeats the ENG-9006 backstop: an unclaimed modal cannot be
 * reported as unclaimed if this branch claims everything. Tightening here is
 * what lets an unrecognised dialog reach `isUnclaimedConfirmDialog` and become
 * visible instead of being blindly confirmed.
 */
export function isMcpServersDialogVisible(screen: string): boolean {
  const block = focusedModalBlock(screen, USAGE_LIMIT_BLOCK_LINES);
  if (!block) return false;
  return block.some((l) => MCP_DIALOG_OPTIONS.some((label) => optionRowDigit(l, label) !== null));
}

/**
 * ENG-9224 — the SAME dialog after Claude Code 2.1.237 replaced the widget.
 *
 * `isMcpServersDialogVisible` above matches a numbered option list. 2.1.237
 * ships a checkbox multi-select instead, with neither the digits nor either of
 * the `MCP_DIALOG_OPTIONS` labels:
 *
 *   3 new MCP servers found in this project
 *   Select any you wish to enable.
 *
 *   MCP servers may execute code or access system resources. All tool calls
 *   require approval. Learn more in the MCP documentation.
 *
 *   ❯ [✓] augmented
 *     [✓] direct-chat
 *     [✓] composio_googlesheets
 *   Space to select · Enter to confirm · Esc to reject all
 *
 * So the branch stopped firing, agents froze on it, and the presence reaper
 * restarted them into the identical prompt until the breaker paused them. Both
 * customer orgs onboarded on 2026-08-20 died this way.
 *
 * This is the second rewrite of this dialog we have chased: ENG-5375 moved the
 * match onto the option TEXT when 2.1.146 reworded the prompt. Matching on text
 * is what left us exposed when the widget itself changed, so this one keys on
 * STRUCTURE — a header, a checked row, a confirm footer — which survives
 * renaming the servers or reordering the list.
 *
 * ## Why a checked row and not just any row
 *
 * `Enter` accepts the CURRENT selection. If Claude Code ever renders this with
 * nothing pre-selected, confirming it enables nothing: the servers stay unbound,
 * the reaper loop continues, and the log says `Auto-accepted MCP servers` while
 * no progress was made. That is strictly worse than today, because it is
 * invisible.
 *
 * So an all-unchecked dialog is deliberately NOT claimed. It falls through to
 * `isUnclaimedConfirmDialog` and becomes a human's problem — the correct outcome
 * for a state we cannot safely resolve by pressing one key.
 */
const MCP_CHECKBOX_HEADER = /new MCP servers? found in this project/i;

/**
 * A CHECKED checkbox row: `❯ [✓] augmented`, `  [x] direct-chat`.
 *
 * `[ ]` is excluded on purpose — see the "why a checked row" note above. The
 * optional `❯` mirrors `optionRowDigit`'s handling of the focus caret.
 */
const MCP_CHECKED_ROW = /^[^\S\n]*(?:❯[^\S\n]*)?\[[✓x]\][^\S\n]*\S/iu;

/**
 * Wider than `USAGE_LIMIT_BLOCK_LINES` because this dialog puts its header
 * above two lines of prose, a blank line, and one row PER SERVER. Six lines
 * reaches the checkboxes but not the header. Twenty covers a comfortable
 * server count while still binding the match to a region rather than the whole
 * capture — the discipline that keeps this file's detectors from becoming the
 * catch-all described above.
 */
const MCP_CHECKBOX_BLOCK_LINES = 20;

export function isMcpCheckboxDialogVisible(screen: string): boolean {
  const block = focusedModalBlock(screen, MCP_CHECKBOX_BLOCK_LINES);
  if (!block) return false;
  if (!block.some((l) => MCP_CHECKBOX_HEADER.test(l))) return false;
  return block.some((l) => MCP_CHECKED_ROW.test(l));
}

function usageLimitModalBlock(screen: string): string[] | null {
  const lines = screen.split('\n');
  // ONLY the nearest footer. Scanning further back was the fourth version of
  // this bug: with a newer dialog on screen and an older usage-limit block
  // still in the scrollback, a backwards search skips the live dialog's footer
  // (no billing row) and matches the STALE one - so we would classify the pane
  // as the usage-limit modal and send a digit + Enter into whatever dialog is
  // actually focused. The live dialog is always the last footer; treating this
  // as "search the capture" rather than "read the focused dialog" is what kept
  // producing near-misses.
  let footer = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i]!.includes('Enter to confirm')) {
      footer = i;
      break;
    }
  }
  if (footer < 0) return null;
  const block = lines.slice(Math.max(0, footer - USAGE_LIMIT_BLOCK_LINES), footer);
  const hasKnownOptionRow = block.some((l) =>
    USAGE_LIMIT_KNOWN_OPTIONS.some((label) => optionRowDigit(l, label) !== null),
  );
  return hasKnownOptionRow ? block : null;
}

/**
 * Detect Claude Code's usage-limit choice dialog (ENG-8213).
 *
 * On hitting the plan limit, Claude Code blocks the TUI on:
 *
 *   What do you want to do?
 *
 *   ❯ 1. Stop and wait for limit to reset
 *     2. Switch to usage credits
 *     3. Switch to Team plan
 *
 *   Enter to confirm · Esc to cancel
 *
 * ...or, on a Claude Team seat that has hit its individual spend limit:
 *
 *   What do you want to do?
 *
 *   ❯ 1. Stop and wait for limit to reset
 *     2. Wait here, then continue automatically at Aug 21, 10am
 *     3. Ask your admin for more usage
 *
 *   Enter to confirm · Esc to cancel
 *
 * Nothing answered it, so the session sat on the modal and the agent was
 * unavailable until a human attached to the pane.
 */
export function isUsageLimitChoiceDialogVisible(screen: string): boolean {
  return usageLimitModalBlock(screen) !== null;
}

/**
 * Locate the option to press by READING ITS NUMBER off the line that carries
 * the option's text — never by assuming it is option 1.
 *
 * This is the whole safety design (ENG-8213). Several rows on this modal cost
 * money or a human's attention: "Switch to usage credits" starts pay-as-you-go
 * spend, "Switch to Team plan" is a plan change, "Ask your admin for more
 * usage" pages a person to raise a budget. Selecting by position would mean a
 * future reordering of this menu silently turns an auto-answer into a purchase.
 * Deriving the digit from the matched text means a reorder just changes which
 * digit we send, and an unrecognised menu yields `null` (answer nothing) rather
 * than a guess.
 *
 * Returns the chosen row's digit (as a tmux key name) and the label it matched,
 * or `null` when no selectable option is present — failing to clear a modal is
 * recoverable; buying credits is not.
 */
export function findUsageLimitSafeOption(
  screen: string,
): { key: string; label: string } | null {
  // Searched inside the modal's own block, so a numbered line elsewhere on the
  // pane can never supply the digit we are about to press.
  const block = usageLimitModalBlock(screen);
  if (!block) return null;
  // Preference order comes from USAGE_LIMIT_SAFE_OPTIONS, not from row order:
  // the OUTER loop is the preference list so `Wait here, then continue
  // automatically` wins wherever it sits on the menu. Iterating rows first
  // would make the answer depend on Claude Code's row ordering, which is the
  // select-by-position mistake this function exists to avoid — just one level
  // up.
  for (const label of USAGE_LIMIT_SAFE_OPTIONS) {
    for (const line of block) {
      const digit = optionRowDigit(line, label);
      if (digit !== null) return { key: digit, label };
    }
  }
  return null;
}

/** The digit alone, as before. */
export function findUsageLimitSafeOptionKey(screen: string): string | null {
  return findUsageLimitSafeOption(screen)?.key ?? null;
}

/**
 * How many option rows a two-choice consent dialog can span. Wider than the
 * rows themselves so the block still reaches the cursor row when Claude Code
 * pads the modal with blank lines (it does, between the prose and the options).
 */
const CONSENT_BLOCK_LINES = 12;

/** Index of the LAST element satisfying `pred`, or -1. */
function lastIndexWhere<T>(xs: readonly T[], pred: (x: T) => boolean): number {
  for (let i = xs.length - 1; i >= 0; i--) {
    if (pred(xs[i]!)) return i;
  }
  return -1;
}

/**
 * True when `line` is a COMPLETE option row for `label` — optionally preceded by
 * the `❯` cursor and/or a `N.` digit, and carrying nothing else.
 *
 * A substring test is not good enough, and the bypass dialog is exactly where
 * that bites. Its detector is `includes('Yes, I accept') && includes('Bypass
 * Permissions')`, and both strings show up in ordinary prose — an agent that
 * reads a manager log, or discusses this very incident, puts them in its own
 * transcript. A substring match would then find the prose line, measure the
 * cursor distance to it, and key that many Downs into whatever dialog is
 * actually focused. Anchoring to the whole row means prose can never be
 * mistaken for a selectable option; at worst we answer nothing.
 *
 * Anchored at both ends deliberately: every label this is called with names a
 * full row ('Yes, I accept', 'Yes, I trust this folder', "Don't ask me again"),
 * unlike USAGE_LIMIT_CONTINUE_OPTION, which is a prefix and is matched by
 * findUsageLimitSafeOption's own looser rule.
 */
/**
 * True when `line` is a menu CURSOR row — the `❯` opens the line (leading
 * whitespace aside) and something follows it.
 *
 * A bare `includes('❯')` is not good enough: the agent's own input prompt
 * renders the same glyph, and so does any prose quoting a menu. Requiring the
 * glyph to START the row is what separates "this is the selected option" from
 * "this line happens to contain an arrow".
 */
function isCursorRow(line: string): boolean {
  return /^[^\S\n]*❯[^\S\n]+\S/.test(line);
}

function isOptionRowFor(line: string, label: string): boolean {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  return new RegExp(
    String.raw`^[^\S\n]*(?:❯[^\S\n]*)?(?:\d\.[^\S\n]*)?` +
      escaped +
      String.raw`[^\S\n]*$`,
  ).test(line);
}

/**
 * ENG-9575: keystrokes that select `label` on the FOCUSED modal, for either
 * shape Claude Code renders — or null when the row isn't there.
 *
 * The bug this exists to kill: every branch below used to answer by DIGIT
 * (`['2','Enter']`) or by BARE ENTER, both of which encode an assumption about
 * how the menu is drawn. Claude Code 2.1.250 draws these consent dialogs as an
 * arrow-select list with NO numbered rows, and — this is the part that turns a
 * cosmetic change into an outage — it defaults the cursor to the REFUSING row:
 *
 *     ❯ No, exit
 *       Yes, I accept
 *     Enter to confirm · Esc to cancel
 *
 * A digit is swallowed (verified on 2.1.250: sending `2` does not move the
 * cursor), so `['2','Enter']` confirms whatever is selected — `No, exit`. Claude
 * exits 0, the manager sees a dead session and respawns, and the next spawn
 * lands on the same dialog. On the two hosts provisioned 2026-08-28 that ran
 * 847 and 1,974 times, every agent down from first boot, while the manager
 * logged `Auto-accepted bypass permissions` on each pass.
 *
 * It is self-perpetuating in a way worth naming: Claude records the consent
 * only after a SUCCESSFUL start, so answering it wrongly also prevents the
 * write that would stop it being asked again. One correct answer ends it for
 * good; a host can never get there on its own.
 *
 * Reading the row instead of assuming its position keeps both shapes working
 * and survives a reorder — the same reasoning as findUsageLimitSafeOption,
 * which derives the digit from the matched text rather than trusting row order.
 * Preserves DEFAULT-DENY: an unrecognised menu returns null, so the caller
 * answers nothing rather than keying a row it cannot see.
 */
export function selectRowKeys(
  screen: string,
  label: string,
): readonly string[] | null {
  // FAIL CLOSED without a focused modal, for BOTH shapes. Every one of these
  // dialogs renders `Enter to confirm` (the numbered shape too — see
  // MCP_DIALOG_OPTIONS above), so no footer means no dialog we are entitled to
  // key. The alternative, searching the whole pane, reads lines the agent
  // itself wrote: a transcript listing the options verbatim ("  2. Yes, I
  // accept") is a complete option row by any structural test, and answering it
  // types into the REPL.
  const block = focusedModalBlock(screen, CONSENT_BLOCK_LINES);
  if (!block) return null;

  // Last match, not first: with an older render of the same dialog still in
  // the block, the live one is the most recent.
  const target = lastIndexWhere(block, (l) => isOptionRowFor(l, label));
  if (target < 0) return null;

  // Numbered shape (pre-2.1.250): the row carries its own digit, so press it.
  // Tried first so a host still on an older Claude Code answers as it always
  // has, and so this fix needs no knowledge of which version a host is running.
  const digit = optionRowDigit(block[target]!, label);
  if (digit !== null) return [digit, 'Enter'];

  // Arrow-select shape: RELATIVE movement, so we need the menu's own cursor.
  // Only a row whose `❯` STARTS the line is a cursor — prose that merely
  // contains the glyph is not, and neither is the agent's input prompt.
  const cursors: number[] = [];
  for (let i = 0; i < block.length; i++) {
    if (isCursorRow(block[i]!)) cursors.push(i);
  }
  // No cursor means we cannot know what Enter would confirm. Answer nothing:
  // on these dialogs the unknown row is the destructive one, and DEFAULT-DENY
  // is the module's contract.
  if (cursors.length === 0) return null;

  // Nearest cursor to the target — option rows are adjacent, so nearest is the
  // menu this row belongs to. A TIE means two candidate menus are equally
  // close and we cannot tell which owns the row; that is ambiguity, and
  // ambiguity is answered with silence rather than a guess.
  let cursor = cursors[0]!;
  let tied = false;
  for (const i of cursors.slice(1)) {
    const d = Math.abs(i - target);
    const best = Math.abs(cursor - target);
    if (d < best) {
      cursor = i;
      tied = false;
    } else if (d === best) {
      tied = true;
    }
  }
  if (tied) return null;

  const distance = target - cursor;
  if (distance === 0) return ['Enter'];
  const step = distance > 0 ? 'Down' : 'Up';
  return [...Array<string>(Math.abs(distance)).fill(step), 'Enter'];
}

/**
 * ENG-9006: the reset moment the modal names, verbatim, or null.
 *
 * The Team seat spend-limit variant renders its auto-continue row as
 *
 *     2. Wait here, then continue automatically at Aug 21, 10am
 *
 * and that tail is the single most authoritative reset string we ever get from
 * Claude Code. Everywhere else we RECONSTRUCT one: the saturated banner often
 * names only an hour ("resets 1am (UTC)") and `banner-parser.ts` resolves it to
 * the next occurrence of that hour, which can never be more than 24h out even
 * when the real reset is days away — the ENG-8901 defect, which had operators
 * scheduling against an instant that was three days early.
 *
 * Returned as the RAW STRING on purpose. Parsing it would mean inventing a
 * second date grammar that can drift from the banner parser's, and guessing a
 * timezone the modal does not state. The one consumer is a sentence shown to a
 * human, and "until Aug 21, 10am" is exactly as useful to them as a Date would
 * be, while being impossible to get subtly wrong.
 *
 * Bounded so a mis-parse cannot push an arbitrary pane line into a message that
 * reaches a customer.
 */
export function findUsageLimitResetHint(screen: string): string | null {
  const block = usageLimitModalBlock(screen);
  if (!block) return null;
  for (const line of block) {
    if (optionRowDigit(line, USAGE_LIMIT_CONTINUE_OPTION) === null) continue;
    const idx = line.indexOf(USAGE_LIMIT_CONTINUE_OPTION);
    const tail = line.slice(idx + USAGE_LIMIT_CONTINUE_OPTION.length).trim();
    // The row reads "… continue automatically at <when>". Drop the connective
    // so the caller can compose "until <when>" without doubling it up.
    const when = tail.replace(/^at\s+/i, '').trim();
    if (!when) return null;
    return when.slice(0, 40);
  }
  return null;
}

/**
 * The usage-limit dialog is on screen but its safe option could not be
 * located, so there is no keystroke we are willing to send (ENG-8213).
 *
 * Callers MUST treat this as "do not key this pane at all" and log it: the
 * generic fallbacks (the watchdog's bounded Enter, acceptDialogs' `❯`
 * readiness branch) would otherwise press Enter on whatever row the cursor
 * happens to be sitting on, and on this dialog most of the rows either spend
 * money or page a human.
 */
export function isUnanswerableUsageLimitDialog(screen: string): boolean {
  return (
    isUsageLimitChoiceDialogVisible(screen) &&
    findUsageLimitSafeOptionKey(screen) === null
  );
}

/**
 * Single-pass dialog recognition. Returns the dismissal action for the
 * first recognised dialog on screen, or `null` when no known dialog is
 * visible (including for the login picker, which must never be keyed
 * past — see isLoginPickerVisible).
 *
 * Branch order mirrors the original acceptDialogs() cascade: the theme
 * picker check must run before any generic `❯`-based readiness logic in
 * callers, since picker rows also render with `❯`.
 */
export function sweepDialogs(screen: string): DialogAction | null {
  // ENG-8213: FIRST, and it must stay first. The usage-limit dialog renders
  // "Enter to confirm", and the mcp-servers branch below fires on
  // `'Enter to confirm' && 'MCP'` — with "MCP" trivially present in the
  // scrollback behind the modal, that branch would match this dialog and
  // send a BARE ENTER, confirming whichever row the cursor is on. Two of
  // those three rows are billing actions. Ordering is the guard.
  if (isUsageLimitChoiceDialogVisible(screen)) {
    const safe = findUsageLimitSafeOption(screen);
    // No recognised safe option => answer nothing. isUnanswerableUsageLimitDialog()
    // lets callers detect this state and stop their own fallbacks keying the pane.
    if (!safe) return null;
    return {
      kind: 'usage-limit-choice',
      // The log names the option ACTUALLY picked, not a fixed literal. With two
      // selectable rows now, a constant string would make a fleet sweep unable
      // to tell an agent that will resume by itself from one parked until a
      // human touches it.
      keys: [safe.key, 'Enter'],
      interKeyDelayMs: 300,
      logMessage: `Auto-answered usage-limit choice dialog (picked '${safe.label}')`,
    };
  }
  // ENG-10619: before the generic branches. The dialog renders
  // `Enter to confirm`, and its default row opts the account INTO model
  // training — so nothing below may get the chance to send a bare Enter.
  // Resolve before claiming (see the folder-trust branch): an unresolvable
  // OFF row falls through rather than suppressing later handlers, and
  // isUnclaimedConfirmDialog() still reports the pane.
  const termsKeys = consumerTermsOffKeys(screen);
  if (termsKeys) {
    return {
      kind: 'consumer-terms',
      keys: termsKeys,
      interKeyDelayMs: 300,
      logMessage: "Auto-answered consumer-terms dialog (picked 'Help improve our AI models: OFF')",
    };
  }
  if (
    screen.includes('Choose the text style') ||
    (screen.includes('Dark mode') && screen.includes('Light mode'))
  ) {
    return {
      kind: 'theme-picker',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted theme picker',
    };
  }
  // ENG-9575: was a bare Enter. On 2.1.250 this dialog's default row is
  // "No, exit", so the bare Enter REFUSED the trust prompt and exited Claude.
  // Latent rather than firing on the hosts that broke (their agent projects
  // were already trusted), but it fires the first time an agent runs in a
  // project directory Claude has not seen — i.e. on every newly provisioned
  // agent. Select the row by name instead.
  //
  // RESOLVE BEFORE CLAIMING. These predicates are whole-pane substring tests,
  // so scrollback or the agent's own prose can match them while a DIFFERENT
  // dialog is focused. Returning null on an unresolved row would cancel the
  // sweep and suppress every branch below — so trust-folder prose sitting
  // behind a real bypass dialog would leave that dialog unanswered, which is
  // the exact wedge this change exists to prevent. Falling through instead
  // keeps the later handlers reachable and lets isUnclaimedConfirmDialog()
  // still report a pane nobody claimed. Same hazard the usage-limit branch
  // documents as the self-gating trap.
  const trustKeys = screen.includes('Yes, I trust this folder')
    ? selectRowKeys(screen, 'Yes, I trust this folder')
    : null;
  if (trustKeys) {
    return {
      kind: 'folder-trust',
      keys: trustKeys,
      interKeyDelayMs: 300,
      logMessage: 'Auto-accepted folder trust',
    };
  }
  // ENG-5364: picks "Don't ask me again", which Claude Code persists in config
  // so subsequent resumes skip the dialog entirely.
  // ENG-9575: was ['3','Enter']. The digit is swallowed on 2.1.250's
  // arrow-select shape, leaving Enter to confirm the default row — here that is
  // "Resume from summary", which is harmless but is NOT the option that stops
  // the dialog recurring, so the agent re-answered it on every single respawn.
  // Resolve before claiming — see the folder-trust branch above.
  const resumeKeys = isResumeModeDialogVisible(screen)
    ? selectRowKeys(screen, "Don't ask me again")
    : null;
  if (resumeKeys) {
    return {
      kind: 'resume-mode',
      keys: resumeKeys,
      interKeyDelayMs: 300,
      logMessage: "Auto-dismissed resume-mode dialog (picked 'Don't ask me again')",
    };
  }
  if (screen.includes('I am using this for local development')) {
    return {
      kind: 'dev-channels',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted dev channels',
    };
  }
  // ENG-9224: both shapes of the same dialog — the pre-2.1.237 numbered list
  // and the 2.1.237 checkbox multi-select. Kept in ONE branch, in the position
  // the numbered one already held, so the ordering hazard ENG-8213 fixed (the
  // usage-limit branch must be evaluated first) is untouched by this change.
  if (isMcpServersDialogVisible(screen) || isMcpCheckboxDialogVisible(screen)) {
    return {
      kind: 'mcp-servers',
      keys: ['Enter'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-accepted MCP servers',
    };
  }
  // ENG-9575: was ['2','Enter'] — the incident branch. See selectRowKeys.
  // Resolve before claiming — see the folder-trust branch above. This one
  // matters most: its two substrings are the ones an agent is likeliest to
  // have written itself, and suppressing the session-feedback branch below
  // leaves a rating prompt swallowing the input box (the koda wedge, ENG-6017).
  const bypassKeys =
    screen.includes('Yes, I accept') && screen.includes('Bypass Permissions')
      ? selectRowKeys(screen, 'Yes, I accept')
      : null;
  if (bypassKeys) {
    return {
      kind: 'bypass-permissions',
      keys: bypassKeys,
      interKeyDelayMs: 300,
      logMessage: 'Auto-accepted bypass permissions',
    };
  }
  // ENG-6017: the rating prompt acts on the bare digit — no Enter needed.
  if (isSessionFeedbackDialogVisible(screen)) {
    return {
      kind: 'session-feedback',
      keys: ['0'],
      interKeyDelayMs: 0,
      logMessage: 'Auto-dismissed session-feedback dialog',
    };
  }
  return null;
}

/**
 * Send a DialogAction's keystrokes to a tmux session, one send-keys call
 * per key with the action's inter-key delay. execFileSync (not execSync)
 * so the session name is an argv entry rather than shell-interpolated.
 */
export async function sendDialogKeys(
  tmuxSession: string,
  action: DialogAction,
): Promise<void> {
  for (let i = 0; i < action.keys.length; i++) {
    if (i > 0 && action.interKeyDelayMs > 0) {
      await new Promise((r) => setTimeout(r, action.interKeyDelayMs));
    }
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, action.keys[i]!], {
      stdio: 'ignore',
    });
  }
}

/**
 * ENG-9006 criterion 3: the block of a focused confirm-footer dialog that NO
 * handler in this module claimed, or null.
 *
 * This is the backstop for the failure mode that has now recurred three times
 * in three weeks, each time the same way: Claude Code reworded a modal, every
 * predicate above went false, the pane wedged, and the fleet found out because
 * a human looked at a screenshot.
 *
 *   2026-07-28  ENG-8213  "Switch to usage credits" / "Switch to Team plan"
 *   2026-08-17  ENG-9006  "Upgrade your plan"           (two-option variant)
 *   2026-08-19  this      "Wait here, then continue automatically at …"
 *                         / "Ask your admin for more usage"   (Team seat spend)
 *
 * Adding each new string is necessary and is not sufficient — the detector is
 * anchored on vendor-authored copy that the vendor keeps changing, so the NEXT
 * rewording is already scheduled. What does not depend on the wording is the
 * shape: a modal is focused (it renders its confirm affordance) and nothing
 * claimed it.
 *
 * DEFAULT-DENY IS PRESERVED. This function keys nothing and returns no action;
 * it exists purely so an unclaimed modal becomes VISIBLE instead of silent.
 * Answering an unrecognised dialog is still forbidden — a future dialog's
 * default row could be destructive, which is the whole reason sweepDialogs()
 * returns null rather than guessing.
 *
 * Deliberately excludes `isUnanswerableUsageLimitDialog`: that state is
 * recognised, is refused on purpose, and already emits its own loud signal.
 * Counting it here too would double-report one pane.
 */
export function focusedConfirmDialogBlock(screen: string): string[] | null {
  const lines = screen.split('\n');
  // The LIVE dialog is the last footer, same rule as usageLimitModalBlock():
  // an older modal still in the scrollback is not what is focused now.
  let footer = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i]!.includes('Enter to confirm')) {
      footer = i;
      break;
    }
  }
  if (footer < 0) return null;
  return lines.slice(Math.max(0, footer - USAGE_LIMIT_BLOCK_LINES), footer + 1);
}

/**
 * True when a focused confirm-footer dialog is on screen and no handler in
 * this module claimed it. See focusedConfirmDialogBlock for why this exists.
 */
export function isUnclaimedConfirmDialog(screen: string): boolean {
  if (focusedConfirmDialogBlock(screen) === null) return false;
  if (sweepDialogs(screen) !== null) return false;
  // Already has its own signal — see the doc above.
  if (isUnanswerableUsageLimitDialog(screen)) return false;
  return true;
}

/**
 * Tiny non-cryptographic hash for hash-only logging of channel input
 * (prod logging policy: input may contain PII/secrets, so log hash+len,
 * never content). Shared by the watchdog and the inject-time hygiene.
 */
export function simpleTextHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h.toString(16);
}

/**
 * ENG-9932: a pane state that NO keystroke this module is willing to send can
 * clear, and that therefore a session RESTART cannot clear either.
 *
 * The distinction this type draws is the whole point of the ticket. On
 * agt-aws-1 (2026-09-03) eight agents were auto-paused because
 * `mcp-presence-reaper` saw "declared MCP(s) have no live children" and
 * restarted the session — three times in ten minutes, into the circuit breaker
 * (ENG-5441). The MCP servers were not broken. Claude Code had never finished
 * booting, because it was sitting on the login picker, so no MCP child had ever
 * been spawned. Every restart re-presented the same modal.
 *
 * `no live MCP children` is therefore a SYMPTOM with (at least) two causes that
 * want opposite remedies:
 *
 *   - an MCP server actually died           -> restart the session (correct)
 *   - the pane is blocked on a modal        -> restarting is futile by
 *                                              construction; surface it
 *
 * Nothing distinguished them, so the reaper applied the first remedy to the
 * second cause and the diagnosis landed two subsystems away from the fault.
 */
/**
 * Every pane state a restart provably cannot recover.
 *
 * A RUNTIME array, with the type DERIVED from it, deliberately. The obvious
 * shape is a bare union plus a hand-written list in the test — but a
 * three-element `UnanswerablePaneKind[]` stays a valid `UnanswerablePaneKind[]`
 * after the union grows to four, so the coverage assertion would keep passing
 * while the new kind shipped untested. A `Record<UnanswerablePaneKind, true>` in
 * the test does not fix it either: `apps/cli/tsconfig.json` excludes
 * `**\/*.test.ts`, and vitest strips types without checking them, so a
 * compile-time guard written in a test file is never evaluated by anything.
 *
 * Exporting the values and deriving the type means adding a kind here makes the
 * suite's coverage check fail at RUNTIME, which is the only mechanism that
 * actually runs.
 */
export const UNANSWERABLE_PANE_KINDS = [
  /** The login picker (ENG-4634). Needs an operator OAuth; no keystroke and no
   *  respawn can complete it, and `acceptDialogs()` deliberately never keys
   *  past it. This is the state marlow was found in. */
  'login-picker',
  /** The usage-limit modal is up but no option we are willing to press is on
   *  it (ENG-8213). Recognised and refused ON PURPOSE — every remaining row
   *  either spends money or pages a human. A respawn re-presents it. */
  'usage-limit-unanswerable',
  /** A modal is focused and NO handler in this module claimed it (ENG-9006).
   *  Unrecognised means neither the channel-input watchdog nor the spawn-time
   *  `acceptDialogs()` cascade can answer it, so a respawn lands on the same
   *  prompt. This is the branch that catches the NEXT dialog Claude Code adds,
   *  which is the one we cannot enumerate in advance. */
  'unclaimed-modal',
] as const;

export type UnanswerablePaneKind = (typeof UNANSWERABLE_PANE_KINDS)[number];

/**
 * Classify a pane that a session restart provably cannot recover, or null.
 *
 * ## Why a RECOGNISED, answerable dialog is deliberately not in this set
 *
 * The tempting version of this function returns non-null for any dialog at
 * all. That would be wrong, and in the dangerous direction: it would add a new
 * way to suppress a restart that is genuinely needed.
 *
 * A dialog `sweepDialogs()` claims gets answered by two independent paths — the
 * per-poll `channel-input-watchdog`, and the spawn-time `acceptDialogs()`
 * cascade in persistent-session.ts. The second one means a restart is a real
 * remedy for an answerable dialog: the fresh session hits the same prompt and
 * the cascade keys past it. So an answerable dialog must keep the pre-ENG-9932
 * behaviour and fall through to the restart.
 *
 * Every kind above is the opposite case: no path answers it, in this session or
 * the next. Holding the restart costs nothing that a restart would have gained,
 * and saves the breaker budget that today gets burned discovering that.
 *
 * DEFAULT-DENY IS PRESERVED, and note the direction. This function keys
 * nothing and returns no `DialogAction`; a pane it does not recognise returns
 * null, which means "carry on with the restart" — i.e. unrecognised falls
 * through to the EXISTING behaviour, never to a new suppression.
 */
export function classifyUnanswerablePane(
  screen: string | null | undefined,
): UnanswerablePaneKind | null {
  // A pane we could not read is not evidence of a blocked pane. Fail open to
  // the legacy restart path: a tmux hiccup must never become a silent,
  // fleet-wide suppression of MCP recovery.
  if (!screen) return null;
  if (isLoginPickerVisible(screen)) return 'login-picker';
  if (isUnanswerableUsageLimitDialog(screen)) return 'usage-limit-unanswerable';
  if (isUnclaimedConfirmDialog(screen)) return 'unclaimed-modal';
  return null;
}

/**
 * A CONTENT-FREE fingerprint of the blocking modal: a stable hash plus the
 * block's length — or null when there is no focused modal block.
 *
 * ## Why a hash and not the text (CodeRabbit, PR #5504)
 *
 * The first version of this returned a 200-character excerpt of the pane. The
 * bound was the wrong control. The modal this fires on most often is the
 * UNCLAIMED one, whose content is by definition unknown to us, and the block is
 * simply the six lines above a confirm footer — which on a busy agent is the
 * transcript it was mid-way through writing. Truncating a customer's message to
 * 200 characters still forwards a customer's message, and `onPaneBlocked`
 * persists this into a control-plane alert row, so it would leave the host.
 *
 * This module's own `simpleTextHash` already states the policy this has to
 * follow — "input may contain PII/secrets, so log hash+len, never content" —
 * and `channel-input-watchdog` already reports exactly this data, the unclaimed
 * dialog block, as `unclaimedDialogHash`. The decision was made; this just
 * failed to follow it.
 *
 * Nothing diagnostic is lost. The `UnanswerablePaneKind` is the actual
 * diagnosis, and the operator-facing line names the one action that resolves
 * it (`tmux attach`), where the real pane is a keystroke away. The hash adds
 * the thing an excerpt could not: correlation. Eight agents blocked on the same
 * unknown modal share a hash, which is how you tell one wedged agent from a
 * fleet-wide dialog rollout.
 */
export function summarizeUnanswerablePane(
  screen: string,
): { hash: string; length: number } | null {
  const block = focusedConfirmDialogBlock(screen);
  if (!block) return null;
  const flat = block.join('\n');
  return { hash: simpleTextHash(flat), length: flat.length };
}
